import { MapPin, Phone, Store } from 'lucide-react';

import { usePharmacyByIdQuery } from '@/entities/rx';
import { Separator } from '@/shared/ui/shadcn/separator';

function formatPharmacyAddress(pharmacy: {
  Address1?: string | null;
  Address2?: string | null;
  City?: string | null;
  State?: string | null;
  ZipCode?: string | null;
}): string | null {
  const line1 = [pharmacy.Address1, pharmacy.Address2].filter(Boolean).join(', ');
  const line2 = [pharmacy.City, pharmacy.State].filter(Boolean).join(', ');
  const parts = [line1, line2, pharmacy.ZipCode].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

interface PharmacyDetailsCardProps {
  pharmacyId: string | undefined;
}

/**
 * PharmacyDetailsCard — displays the pharmacy name, address, and phone
 * for a given DoseSpot pharmacy ID.
 */
export function PharmacyDetailsCard({ pharmacyId }: PharmacyDetailsCardProps) {
  const pharmacyQuery = usePharmacyByIdQuery(pharmacyId);
  const pharmacy = pharmacyQuery.data;

  if (!pharmacy) return null;

  const address = formatPharmacyAddress(pharmacy);
  const hasContent = pharmacy.StoreName ?? address ?? pharmacy.PrimaryPhone;
  if (!hasContent) return null;

  return (
    <div className="px-4 pb-2">
      <div className="rounded-lg border bg-muted/40 px-3 py-2.5 text-sm">
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Pharmacy
        </p>
        <div className="space-y-1 text-foreground">
          {pharmacy.StoreName ? (
            <div className="flex items-center gap-2">
              <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="font-medium">{pharmacy.StoreName}</span>
            </div>
          ) : null}
          {address ? (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span>{address}</span>
            </div>
          ) : null}
          {pharmacy.PrimaryPhone ? (
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span>{pharmacy.PrimaryPhone}</span>
            </div>
          ) : null}
        </div>
      </div>
      <Separator className="mt-3" />
    </div>
  );
}
